import { ReactNode } from 'react';
import { tv } from 'tailwind-variants';

import {
  FromStore,
  Job,
  PaginatedAsyncState,
  Variation,
} from '@cartwhl/common/types';
import { getStudioUrl } from '@cartwhl/common/utils';
import EmptyState from '@cartwhl/ui/messaging/EmptyState';

import GalleryCard from './GalleryCard';

const list = tv({
  variants: {
    variant: {
      grid: 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4',
      list: 'flex flex-col flex-nowrap gap-8',
    },
  },
  defaultVariants: {
    variant: 'list',
  },
});

export default function GalleryList({
  asyncState,
  className,
  variant = 'list',
  maxRecords,
  maxPlaceholders,
  onCardRender,
  autoMarquee = true,
}: {
  asyncState: FromStore<PaginatedAsyncState<Job[]>>;
  className?: string;
  variant?: 'grid' | 'list';
  maxRecords?: number;
  maxPlaceholders?: number;
  onCardRender?: (job: Job, variation: Variation) => ReactNode;
  autoMarquee?: boolean;
}) {
  let records = asyncState.data?.items || [];
  const status = asyncState.status;

  if (typeof maxRecords === 'number') {
    records = records.slice(0, maxRecords);
  }
  if (typeof maxPlaceholders === 'undefined') {
    maxPlaceholders = maxPlaceholders || 1;
  }
  return (
    <>
      {status === 'ready' && records.length === 0 && (
        <EmptyState className="w-full flex-none" description="No results found" />
      )}
      <div className={list({ variant, className })}>
        {records.flatMap((job) =>
          Object.entries(job.variationInfoByID).map(([key, variation]) => (
            <GalleryCard
              key={job.jobID + key}
              variant={variant}
              title={variation.prompt}
              src={variation.mp4Path}
              url={`${getStudioUrl()}/home?jobID=${
                job.jobID
              }&animationNumber=${variation.variationID}`}
              isMarquee={autoMarquee}
            >
              {onCardRender?.(job as Job, variation)}
            </GalleryCard>
          )),
        )}

        {status === 'pending' &&
          [...Array(maxPlaceholders)].map((_, i) => (
            <GalleryCard key={i} variant={variant} className="animate-pulse" />
          ))}
      </div>
    </>
  );
}
